import { Container } from '@mui/material'
import Sidebar from './components/Sidebar'
import Subnav from './components/Subnav'
import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Typography } from '../Typography'
import { AchieveLink } from '../AchieveLink'
import Image from 'next/image'
import MenuIcon from 'components/Svgs/Menu.js'
import { Button } from '@achieve/ascend'
import { rewriteAssetUrl } from '@/utils/conversions'
import { useCurrentUrl } from '@/hooks/useCurrentUrl'
import HeaderSticky from './components/HeaderSticky'
import { useAppRouter } from '@/hooks/useAppRouter/useAppRouter'
import useScrollBelow from '@/hooks/useScrollBelow'
import useLayoutMenuCondensed from '@/hooks/useLayoutMenuCondensed'
import useLastScrollDirection from '@/hooks/useLastScrollDirection'
import { FdrPage } from 'utils/environmentConfig'
import { FDRHeader } from './components/FDRHeader'
import { mainProductConfig } from 'constants/mainProductConfig'
import { STICKY_HEADER_CTA_VIEWPORT_IDS } from 'constants/header'
import useScrollThreshold from '@/hooks/useScrollThreshold'
import { useClientDashboardLoginOverlayTrigger } from '@/hooks/useClientDashboardLoginOverlayTrigger/useClientDashboardLoginOverlayTrigger'
import { useAnalyticsTrackEvent } from '@/hooks/useAnalyticsTrackEvent'
import { useAnyElementInViewport } from '@/hooks/useAnyElementInViewport'
import { ActiveNavProvider, useActiveNavValue } from './context/ActiveNavContext'

const STICKY_HEADER_BLACKLIST = []
const CLIENT_DASHBOARD_LOGIN_TOKEN = '@clientDashboardLogin'

const StickyRender = ({
  showStickyHeader,
  PartnerLogo,
  dynamicStickyNav,
  footerSticky,
  lastScrollDirection,
  isBelowStickyThreshold,
  showSticky,
  siteEnv,
  linkUrl,
  phoneData,
  logoLink,
  logoEvent,
}) => {
  if (!showStickyHeader) return null

  const tp_image = dynamicStickyNav
    ? dynamicStickyNav?.content?.fields?.cardImage
    : footerSticky?.card?.image
  const tp_text = dynamicStickyNav ? null : footerSticky?.card?.subtitle

  const isVisible = lastScrollDirection != 'top' && isBelowStickyThreshold && showSticky

  if (siteEnv === FdrPage) {
    return (
      <FDRHeader
        siteEnv={siteEnv}
        tp_text={tp_text}
        tp_image={tp_image}
        linkUrl={dynamicStickyNav ? dynamicStickyNav?.link?.fields?.linkHref : linkUrl}
        phoneData={phoneData}
        mainProductConfig={mainProductConfig}
        backLink="/"
        logoEvent={logoEvent}
        logoLink={logoLink}
        ariaHidden={!isVisible}
        className={classNames(
          'transform-gpu transition-[transform,opacity] duration-500 ease-in-out will-change-transform motion-reduce:transition-none',
          {
            'translate-y-0 opacity-100': isVisible,
            'pointer-events-none -translate-y-full opacity-0': !isVisible,
          }
        )}
        partnerLogo={PartnerLogo}
      />
    )
  }

  const stickyClassName = classNames('top-0', {
    block: lastScrollDirection != 'top' && isBelowStickyThreshold,
    'hidden!': lastScrollDirection === 'top' && isBelowStickyThreshold,
  })

  return (
    <HeaderSticky
      CurrentSvg={<PartnerLogo />}
      tp_title={
        dynamicStickyNav
          ? dynamicStickyNav?.content?.fields?.cardTitleText
          : footerSticky?.card?.title
      }
      tp_image={tp_image}
      tp_text={tp_text}
      linkUrl={dynamicStickyNav ? dynamicStickyNav?.link?.fields?.linkHref : '/'}
      linkText={
        dynamicStickyNav
          ? dynamicStickyNav?.link?.fields?.linkText?.content[0]?.content[0]?.value
          : 'Get Started'
      }
      subLinkText={dynamicStickyNav ? dynamicStickyNav?.linkSubtext : 'From Freedom Debt Relief'}
      className={stickyClassName}
    />
  )
}

const NavItemLabel = ({ label, isHighlighted, isCurrentPage }) => {
  return (
    <div
      className="flex flex-col items-center gap-[10px] pt-2"
      data-active={isCurrentPage || undefined}
    >
      <Typography
        content={label}
        variant="bodyMd"
        className={classNames(
          'whitespace-nowrap font-medium! text-[18px]! transition-colors duration-200',
          isHighlighted ? 'text-blue-900' : 'text-black'
        )}
      />
      <span
        aria-hidden="true"
        className={classNames(
          'block h-[4px] w-full rounded-t-[2px] transition-colors duration-200',
          {
            'bg-blue-900': isHighlighted,
            'bg-transparent': !isHighlighted,
          }
        )}
      />
    </div>
  )
}

export const transformExternalContent = (content) => {
  if (!content) return undefined

  if (
    content?.sys?.contentType?.sys?.id === 'wwwArticlePages' ||
    content?.sys?.contentType?.sys?.id === 'wwwBlogPosts'
  ) {
    return {
      cardTitleText: content.fields?.title,
      cardText: content.fields?.metaDescription,
      cardImage: rewriteAssetUrl(content.fields?.image?.fields?.file?.url),
    }
  }

  return undefined
}

export const transformLongFormContent = (content) => {
  if (!content) return undefined

  return {
    cardTitleText: content.fields?.title,
    cardText: content.fields?.hook,
    cardImage: rewriteAssetUrl(content.fields?.mainImage?.fields?.mediaContent?.fields?.file?.url),
  }
}

export const transformArticleCtaCard = (content) => {
  if (!content) return undefined

  return {
    cardTitleText: content.fields?.title?.content[0]?.content[0]?.value,
    cardText: content.fields?.subtitle?.content[0]?.content[0]?.value,
    cardImage: rewriteAssetUrl(content.fields?.image?.fields?.mediaContent?.fields?.file?.url),
  }
}

export const transformArticleCardContent = (fields) => {
  if (fields.externalContent?.content) {
    return transformExternalContent(fields.externalContent.content)
  }

  const contentTypeId = fields.content?.sys?.contentType?.sys?.id

  if (contentTypeId === 'longFormContent') {
    return transformLongFormContent(fields.content)
  }

  if (contentTypeId === 'articleCtaCard') {
    return transformArticleCtaCard(fields.content)
  }

  return undefined
}

/**
 * Transforms Contentful megamenu data to the menuData format
 * @param {Object} contentfulData - The Contentful megamenu entry
 * @param {string|null} currentURL - Current browser URL used to filter navigation items
 * @returns {Object} - The transformed menuData object
 */
export const transformContentfulMegamenu = (contentfulData, currentURL = null) => {
  if (!contentfulData) return {}

  const visitedIds = new Set()
  const url = currentURL ?? ''
  const toList = (value) =>
    Array.isArray(value) ? value.filter(Boolean).map(String) : value ? [String(value)] : []
  const isVisible = (fields) => {
    if (!currentURL) return true
    const whitelist = toList(fields.whitelistUrl)
    const blacklist = toList(fields.blacklistUrl)
    if (whitelist.length > 0 && !whitelist.some((value) => value === url)) return false
    if (blacklist.length > 0 && blacklist.some((value) => value === url)) return false
    return true
  }

  // Transform navigation item recursively
  const transformNav = (item) => {
    if (!item?.fields) return null

    const entryId = item.sys?.id
    if (entryId && visitedIds.has(entryId)) return null // Skip circular reference

    const { fields } = item
    if (!fields.variant) return null
    if (!isVisible(fields)) return null

    // Track only after validation so early returns cannot poison sibling traversal.
    if (entryId) visitedIds.add(entryId)

    const transformed = {
      variant: fields.variant,
      id:
        entryId ||
        (fields.label
          ? fields.label
              .toLowerCase()
              .replace(/\s+/g, '-')
              .replace(/[^a-z0-9-]/g, '')
          : undefined),
      ...(fields.label !== undefined && { label: fields.label }),
      ...(fields.link && { link: fields.link }),
      ...(fields.description && { description: fields.description }),
      ...(fields.icon && { icon: rewriteAssetUrl(fields.icon?.fields?.file?.url) }),
      ...(fields.viewAllLabel && { viewAllLabel: fields.viewAllLabel }),
      ...(fields.columns != null && { columns: fields.columns }),
      ...(fields.variant === 'articleCard' &&
        (fields.content || fields.externalContent?.content) && {
          content: transformArticleCardContent(fields),
        }),
    }

    // Transform children
    if (fields.children?.length) {
      const children = fields.children
        .filter(
          (child) =>
            typeof child === 'object' && child !== null && !String(child).includes('[Circular')
        )
        .map((child) => transformNav(child))
        .filter(Boolean)
      if (!children.length) {
        if (entryId) visitedIds.delete(entryId)
        return null
      }
      transformed.children = children
    }

    if (entryId) visitedIds.delete(entryId)
    return transformed
  }

  // Extract logo
  const logoUrl = contentfulData.logo?.fields?.mediaContent?.fields?.file?.url
  const partnerLogo = logoUrl
    ? logoUrl.startsWith('//')
      ? `https:${logoUrl}`
      : logoUrl
    : '/fdr-logo.svg'
  const partnerLogoAlt =
    contentfulData.logo?.fields?.mediaContent?.fields?.title ||
    contentfulData.logo?.fields?.name ||
    'FDR Logo'

  // Transform CTAs
  const transformCta = (cta) => ({
    label: cta.fields?.label || '',
    link: cta.fields?.link || '',
    style: cta.fields?.style || 'primary',
    show: cta.fields?.showOn || 'web',
    section: cta.fields?.section || 'ButtonsHeader',
    linkText: cta.fields?.linkText || '',
    linkType: cta.fields?.linkType || '',
    icon: cta.fields?.icon ? rewriteAssetUrl(cta.fields?.icon?.fields?.file?.url) : null,
  })

  return {
    partnerLogo,
    partnerLogoAlt,
    topCtas:
      contentfulData.topCtas
        ?.filter((cta) => cta?.fields && isVisible(cta.fields))
        .map(transformCta)
        .filter((cta) => cta.section === 'ButtonsHeader') || [],
    sidebarCtas:
      contentfulData.sidebarCtas
        ?.filter((cta) => cta?.fields && isVisible(cta.fields))
        .map(transformCta)
        .filter((cta) => cta.section === 'ButtonsHeader') || [],
    aboveHeaderCtas:
      contentfulData.topCtas
        ?.map(transformCta)
        .filter((cta) => cta.section === 'ButtonsAboveHeader') || [],
    navigation: contentfulData.navigation?.map(transformNav).filter(Boolean) || [],
  }
}

export const MegaMenu = ({
  content,
  footerSticky,
  dynamicStickyNav,
  siteEnv,
  customPhoneData = null,
  logoLink = true,
  logoEvent,
}) => {
  const phoneData = customPhoneData
  const currentURL = useCurrentUrl()
  const [hoverNavigation, setHoverNavigation] = useState(false)
  const [hoverSubnav, setHoverSubnav] = useState(false)
  const [openSubnav, setOpenSubnav] = useState(false)
  const [openSidebar, setOpenSidebar] = useState(false)
  const [dataSubnav, setDataSubnav] = useState(null)
  const [hoveredNavId, setHoveredNavId] = useState(null)
  const menuData = useMemo(
    () => transformContentfulMegamenu(content, currentURL),
    [content, currentURL]
  )
  const partnerLogo = menuData?.partnerLogo
  const partnerLogoAlt = menuData?.partnerLogoAlt
  const topCtas = menuData?.topCtas || []
  const navigation = menuData?.navigation || []
  const aboveHeaderCtas = menuData?.aboveHeaderCtas || []
  const sidebarCtas = menuData?.sidebarCtas || []
  const activeNav = useActiveNavValue(navigation, currentURL)
  const { dynamicPathname } = useAppRouter()
  const mainCTA = topCtas.find((cta) => cta?.linkType === 'MainCTA')
  const showStickyHeader =
    !STICKY_HEADER_BLACKLIST.includes(dynamicPathname) &&
    (footerSticky?.card?.title || dynamicStickyNav?.content?.fields?.cardTitleText)

  // Show sticky section only after scrolling past 50px of the page
  const isBelowStickyThreshold = useScrollBelow(50)
  const lastScrollDirection = useLastScrollDirection()
  const lastScrollDirectionZero = useLastScrollDirection(0)
  const [, setLayoutMenuCondensed] = useLayoutMenuCondensed()
  const isScrolling = useScrollThreshold(100)

  const isScrolledPast150 = useScrollBelow(150)
  const ctasAreInViewport = useAnyElementInViewport(STICKY_HEADER_CTA_VIEWPORT_IDS, 0)

  const { analyticsTrackEvent } = useAnalyticsTrackEvent()
  const { openOverlayInsteadOfNavigating } = useClientDashboardLoginOverlayTrigger()

  const isClientDashboardLoginLink = useCallback((url) => url === CLIENT_DASHBOARD_LOGIN_TOKEN, [])

  const handleButtonClick = useCallback(
    //todo changed as CTA button, but destination url is https://fdr-dev.programdashboard.com/login
    (href, name, isLeadCta) => {
      analyticsTrackEvent({
        nav_link_section: 'Header',
        click_type: 'Button Click',
        click_id: name,
        click_url: href,
        click_text: `FDR-Web | ${name}`,
        track_event: 'global_header',
        event_type: isLeadCta ? 'conversion' : 'track',
      })
    },
    [analyticsTrackEvent]
  )

  const handleCtaClick = useCallback(
    (e, url, label) => {
      handleButtonClick(url, label, false)
      if (isClientDashboardLoginLink(url)) {
        openOverlayInsteadOfNavigating(e)
        return
      }
      window.location.href = url
    },
    [isClientDashboardLoginLink, openOverlayInsteadOfNavigating, handleButtonClick]
  )

  useEffect(() => {
    // Toggle header condensation based on scroll position
    setLayoutMenuCondensed(isBelowStickyThreshold)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBelowStickyThreshold])

  useEffect(() => {
    if (isScrolling) {
      setHoverNavigation(false)
      setHoverSubnav(false)
      setOpenSubnav(false)
      setHoveredNavId(null)
    }
  }, [isScrolling])

  const PartnerLogo = () => {
    return (
      <div className={'w-[85px] h-[40px] lg:w-[110px] lg:h-[50px]'}>
        <Image
          src={partnerLogo}
          alt={partnerLogoAlt}
          className={'h-full w-full'}
          height="50"
          width="110"
          priority={true}
        />
      </div>
    )
  }

  const HeaderButton = ({ label, url, style, buttonTextVariant, ...props }) => {
    return (
      <Button
        onClick={(e) => handleCtaClick(e, url, label)}
        size="small"
        className={classNames(`px-4 py-2 rounded-md h-[30px]`, {
          'bg-[#041B93] border-2 border-[#041B93] border-solid text-white': style === 'primary',
          'bg-[#EFF5FF] border-2 border-[#EFF5FF] border-solid text-[#041B93]':
            style === 'secondary',
          'bg-white border-2 border-[#041B93] border-solid text-[#041B93]': style === 'outline',
        })}
        {...props}
      >
        <Typography
          content={label}
          variant={buttonTextVariant}
          fontWeight="bold"
          className="whitespace-nowrap"
        />
      </Button>
    )
  }

  const SidebarButton = ({ label, url, icon, style, buttonTextVariant, ...props }) => {
    return (
      <Button
        onClick={(e) => handleCtaClick(e, url, label)}
        size="small"
        className={classNames(`p-4 rounded-md h-[30px]`, {
          'bg-[#041B93] border-2 border-[#041B93] border-solid text-white': style === 'primary',
          'bg-[#cb000e] border-2 border-[#cb000e] border-solid text-white': style === 'red-primary',
          'bg-[#EFF5FF] border-2 border-[#EFF5FF] border-solid text-[#041B93]':
            style === 'secondary',
          'bg-white border-2 border-[#041B93] border-solid text-[#041B93]': style === 'outline',
          'bg-white border-2 border-0 text-[#041B93]': style === 'text',
        })}
        {...props}
      >
        {icon && <Image src={icon} alt={label} width={20} height={20} className="mr-2" />}
        <Typography
          content={label}
          variant={buttonTextVariant}
          fontWeight="bold"
          className="whitespace-nowrap"
        />
      </Button>
    )
  }

  const SidebarButtons = () => {
    return (
      <div className="flex flex-col gap-2 justify-center items-justify w-full lg:w-auto">
        {sidebarCtas.map((cta) => (
          <SidebarButton
            key={cta.label}
            label={cta.label}
            url={cta.link}
            style={cta.style}
            buttonTextVariant="bodyMd"
            icon={cta.icon}
          />
        ))}
      </div>
    )
  }

  const handleOpenSidebar = () => {
    setOpenSidebar(!openSidebar)
  }

  useEffect(() => {
    if (hoverNavigation || hoverSubnav) {
      setOpenSubnav(true)
    } else {
      setOpenSubnav(false)
    }
  }, [hoverNavigation, hoverSubnav])

  return (
    <ActiveNavProvider value={activeNav}>
      <div
        className={classNames('hidden min-h-[100px]', {
          'block!': lastScrollDirectionZero === 'top' && isBelowStickyThreshold,
        })}
      ></div>
      <header
        data-testid="mega-menu"
        className={classNames('[&_a]:no-underline [&_a:hover]:no-underline', {
          'fixed top-0 left-0 right-0 z-50':
            lastScrollDirectionZero === 'top' && isBelowStickyThreshold,
          'shadow-[0_2px_2px_-2px_rgba(0,0,0,0.25)]': isBelowStickyThreshold,
        })}
      >
        <Container
          maxWidth={true}
          disableGutters
          className={classNames(
            'max-w-[1440px] lg:h-[100px] px-2 lg:px-8 bg-white py-1 lg:pb-0 lg:pt-2',
            'flex flex-col gap-2'
          )}
          data-narrow={true}
        >
          <div className="hidden lg:block">
            {aboveHeaderCtas.map((cta) => (
              <div key={cta.label} className="flex flex-row gap-2 justify-end w-full">
                <Typography variant="bodySm" content={cta.label} />
                <AchieveLink
                  href={cta.link}
                  className="cursor-pointer mt-[-2px]"
                  track={{
                    click_id: cta.label,
                    click_text: cta.label,
                    track_event: 'mega_menu',
                    event_action: 'link_click',
                    event_type: 'link_click',
                  }}
                >
                  <Typography
                    className="font-bold! text-[#154199]"
                    variant="bodySm"
                    content={cta.linkText}
                  />
                </AchieveLink>
              </div>
            ))}
          </div>
          <div className="flex flex-row gap-4 lg:justify-between w-full">
            <div className="lg:hidden flex flex-col justify-center items-center">
              <button
                type="button"
                aria-label={openSidebar ? 'Close menu' : 'Open menu'}
                aria-expanded={openSidebar}
                onClick={handleOpenSidebar}
                className="border-none bg-transparent"
              >
                <MenuIcon />
              </button>
            </div>
            <div className="flex items-center justify-center w-[85px] lg:w-[130px] lg:translate-y-[-12px]">
              <AchieveLink
                href="/"
                track={{
                  click_id: 'Header logo',
                  click_text: 'Achieve-Web | Header logo',
                  track_event: 'mega_menu',
                  event_action: 'link_click',
                  event_type: 'link_click',
                }}
              >
                <PartnerLogo />
              </AchieveLink>
            </div>
            <div className="hidden lg:flex lg:flex-col w-full justify-center">
              <div
                className="flex flex-row w-full gap-2 xl:gap-[clamp(1rem,2vw,2rem)] justify-center items-center cursor-pointer"
                id="mega-menu-navigation"
                onMouseLeave={() => {
                  setHoverNavigation(false)
                  setHoveredNavId(null)
                }}
              >
                {navigation?.map((item) => {
                  const hasChildren = item?.children?.length > 0
                  const isCurrentPage = activeNav.isActiveBranch(item)
                  // Keep the trigger highlighted while its own subnav stays open,
                  // even once the pointer has moved down into the panel.
                  const isHovered =
                    hoveredNavId === item.id || (openSubnav && dataSubnav?.id === item.id)
                  const isHighlighted = isHovered || isCurrentPage
                  const handleMouseEnter = () => {
                    setHoveredNavId(item.id)
                    if (hasChildren) {
                      setHoverNavigation(true)
                      setDataSubnav(item)
                      setHoverSubnav(true)
                    } else {
                      setHoverNavigation(false)
                      setHoverSubnav(false)
                    }
                  }

                  if (hasChildren) {
                    return (
                      <button
                        type="button"
                        key={item.label}
                        onMouseEnter={handleMouseEnter}
                        onFocus={handleMouseEnter}
                        onClick={handleMouseEnter}
                        aria-expanded={isHovered}
                        aria-haspopup="true"
                        className="cursor-pointer border-none bg-transparent p-0"
                      >
                        <NavItemLabel
                          label={item.label}
                          isHighlighted={isHighlighted}
                          isCurrentPage={isCurrentPage}
                        />
                      </button>
                    )
                  }

                  return (
                    <AchieveLink
                      key={item.label}
                      href={item.link}
                      onMouseEnter={handleMouseEnter}
                      aria-current={isCurrentPage ? 'page' : undefined}
                      track={{
                        click_id: item.label,
                        click_text: item.label,
                        nav_link_section: 'Mega Menu',
                        track_event: 'mega_menu',
                        event_action: 'link_click',
                        event_type: 'link_click',
                      }}
                    >
                      <NavItemLabel
                        label={item.label}
                        isHighlighted={isHighlighted}
                        isCurrentPage={isCurrentPage}
                      />
                    </AchieveLink>
                  )
                })}
              </div>
            </div>
            <div className="flex flex-col gap-2 justify-center items-end w-full lg:w-auto">
              <div className="hidden lg:flex flex-row gap-2 justify-end items-center">
                {topCtas
                  .filter((cta) => cta.show === 'web')
                  .map((cta) => (
                    <HeaderButton
                      key={cta.label}
                      label={cta.label}
                      url={cta.link}
                      style={cta.style}
                      buttonTextVariant="bodySm"
                    />
                  ))}
              </div>
              <div className="flex lg:hidden flex-row gap-2 justify-end items-center">
                {topCtas
                  .filter((cta) => cta.show === 'mobile')
                  .map((cta) => (
                    <HeaderButton
                      key={cta.label}
                      label={cta.label}
                      url={cta.link}
                      style={cta.style}
                      buttonTextVariant="bodyXs"
                    />
                  ))}
              </div>
            </div>
          </div>
        </Container>
        <StickyRender
          showStickyHeader={showStickyHeader}
          PartnerLogo={<PartnerLogo />}
          dynamicStickyNav={dynamicStickyNav}
          footerSticky={footerSticky}
          lastScrollDirection={lastScrollDirection}
          isBelowStickyThreshold={isBelowStickyThreshold}
          showSticky={!ctasAreInViewport && isScrolledPast150}
          siteEnv={siteEnv}
          linkUrl={mainCTA?.link}
          phoneData={phoneData}
          logoLink={logoLink}
          logoEvent={logoEvent}
        />
        {navigation
          .filter((item) => item?.children?.length > 0)
          .map((item) => {
            const hidden = !openSubnav || dataSubnav?.id !== item.id

            return (
              <Subnav
                key={`${item.id}:${currentURL ?? ''}`}
                setHoverSubnav={setHoverSubnav}
                setHoverNavigation={setHoverNavigation}
                data={item}
                hidden={hidden}
              />
            )
          })}
        <Sidebar
          iconHeader={<PartnerLogo />}
          open={openSidebar}
          handleClose={handleOpenSidebar}
          bottomCtas={<SidebarButtons />}
          navigationItems={navigation}
        />
      </header>
    </ActiveNavProvider>
  )
}
